"use client";

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Package, Plus } from "lucide-react";
import ReusableServerDataGrid from "../../../../Components/ReusableServerDataGrid";
import CrudPageHeader from "../../../../Components/CrudPageHeader";
import { AddAnElementButton } from "../../../../Components/AddElementsButton";
import { ApiLink } from "../../../../APILink/ApiLink";
import authHeader from "../../../../Auth/authHeader";
import {
  errorAlert,
  handleSuccess,
  infoAlert,
} from "../../../../Components/Alerts";
import BarcodeGeneratorModal from "../../../../Components/GenerateBarcode";
import { CompoundArticleModal } from "./CompoundArticleForm";
import CompoundArticleViewModal from "./CompoundArticleViewModal";
import { getCompoundArticleErrorMessage } from "./CompoundArticleService";
import ArticleGridHeader from "../../../../CRUD/Article/ArticleGridHeader";

export default function CompoundArticlePage() {
  const headers = authHeader();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [rowCount, setRowCount] = useState(0);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [viewItemId, setViewItemId] = useState(null);

  const [openBarcodeModal, setOpenBarcodeModal] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [quickFilter, setQuickFilter] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${ApiLink.API_URL}CompoundArticle/getArticlesGrid`, {
        headers,
        params: {
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          sort: JSON.stringify(sortModel),
          filter: JSON.stringify(filterModel.items),
          quickFilter: quickFilter.join(" "),
        },
      });
      setData(response.data.items || []);
      setRowCount(response.data.total || 0);
    } catch (error) {
      errorAlert(getCompoundArticleErrorMessage(error, "Gabim gjatë marrjes së artikujve të përbërë."));
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleEditItem = (item) => {
    setEditingItem(item.ArticleID);
    setIsModalOpen(true);
  };

  const handleViewItem = (item) => {
    setViewItemId(item.ArticleID);
    setIsViewOpen(true);
  };

  const handleDeleteItem = async (deleteItemId) => {
    setLoading(true);
    try {
      const response = await axios.delete(
        `${ApiLink.API_URL}CompoundArticle/${deleteItemId}`,
        { headers },
      );

      const result = response?.data;
      if (result?.success) {
        handleSuccess("Artikulli i përbërë u fshi me sukses.");
        await fetchData();
        return;
      }

      errorAlert(
        getCompoundArticleErrorMessage(
          { response: { data: result?.message } },
          "Ky artikull nuk mund të fshihet sepse është përdorur në transaksione.",
        ),
      );
    } catch (error) {
      errorAlert(getCompoundArticleErrorMessage(error, "Gabim gjatë fshirjes së artikullit të përbërë."));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenBarcode = (article) => {
    setSelectedArticle(article);
    setOpenBarcodeModal(true);
  };

  const handleCloseBarcode = () => {
    setSelectedArticle(null);
    setOpenBarcodeModal(false);
  };

  const articleColumns = useMemo(() => {
    return ArticleGridHeader({ handleOpenModal: handleOpenBarcode }).map((column) => {
      if (column.field === "ArticleName") {
        return { ...column, width: 270 };
      }

      if (column.field === "ArticleProduction") {
        return {
          ...column,
          headerName: "Lloji",
          width: 140,
          filterable: true,
          sortable: true,
        };
      }

      if (column.field === "IsActive") {
        return {
          ...column,
          headerName: "Statusi",
          filterType: "select",
          filterOptions: [
            { value: "true", label: "Aktiv" },
            { value: "false", label: "Joaktiv" },
          ],
        };
      }

      if (column.field === "TouchScreen") {
        return {
          ...column,
          headerName: "POS",
          filterType: "select",
          filterOptions: [
            { value: "true", label: "POS Aktiv" },
            { value: "false", label: "POS Joaktiv" },
          ],
        };
      }

      if (column.field === "flags") {
        return {
          ...column,
          sortable: false,
          filterable: false,
        };
      }

      return column;
    });
  }, [handleOpenBarcode]);

  const rows = useMemo(() => {
    return data.map((item, index) => ({
      id: paginationModel.page * paginationModel.pageSize + index + 1,
      Barcode: item.Barcode,
      ArticleName: item.ArticleName,
      ArticleProduction:
        item.ArticleProduction === 2
          ? "Gjysëm Produkt"
          : item.ArticleProduction === 3
            ? "Produkt i Prodhuar"
            : "-",
      PurchasePrice: item.PurchasePrice,
      SalesPrice: item.SalesPrice,
      IsActive: item.IsActive,
      TouchScreen: item.TouchScreen,
      deleteItemId: item.ArticleID,
      rowInfo: item,
      ArticleID: item.ArticleID,
    }));
  }, [data, paginationModel.page, paginationModel.pageSize]);

  useEffect(() => {
    fetchData();
  }, [paginationModel, sortModel, filterModel, quickFilter]);

  return (
    <div className="ArticlePage__home-page">
      <CrudPageHeader
        icon={Package}
        title="Menaxhimi i Artikujve të Përbërë"
        description="Menaxhoni, shtoni, modifikoni dhe fshini artikujt e përbërë me një rrjedhë moderne dhe të qartë."
        tutorialLabel="Shiko Tutorialin"
        onTutorialClick={() => infoAlert("Tutorial së shpejti!")}
        action={
          <AddAnElementButton
            className="AP__btn-primary"
            text="Artikull I Ri"
            icon={<Plus size={16} />}
            onClick={handleAddItem}
          />
        }
        className="AP__header"
      />

      <CompoundArticleModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={fetchData}
        itemToEdit={editingItem}
      />

      <CompoundArticleViewModal
        isOpen={isViewOpen}
        onClose={() => {
          setIsViewOpen(false);
          setViewItemId(null);
        }}
        articleId={viewItemId}
      />

      <ReusableServerDataGrid
        columns={articleColumns}
        rows={rows}
        onEdit={handleEditItem}
        onDelete={handleDeleteItem}
        onView={handleViewItem}
        showViewAction={true}
        showPayment={false}
        actionsMinWidth={90}
        actionsFlex={0.75}
        height={500}
        rowCount={rowCount}
        loading={loading}
        paginationModel={paginationModel}
        setPaginationModel={setPaginationModel}
        sortModel={sortModel}
        setSortModel={setSortModel}
        filterModel={filterModel}
        setFilterModel={setFilterModel}
        quickFilter={quickFilter}
        setQuickFilter={setQuickFilter}
      />

      <BarcodeGeneratorModal
        open={openBarcodeModal}
        handleClose={handleCloseBarcode}
        articleData={selectedArticle}
      />
    </div>
  );
}
